/* eslint-disable react/require-default-props */
import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

import Modal from '../../../../../components/Modal/Modal';
import AddressFormModal from '../../../../Comercial/ClienteShow/components/AddressFormModal';
import { createCustomer } from '../../../../../services/customers/customersApi';
import {
   FormGrid,
   Field,
   FieldLabel,
   FieldInput,
   FieldError,
   FieldHint,
   GhostButton,
   PrimaryButton,
} from './styles';

// ===========================================================================
// NewCustomerModal — cadastro de cliente com dados básicos (nome, doc, contato)
// feito SEM sair do fluxo de criação do item. Em duas etapas encadeadas:
//   1) cria o cliente (POST /customers);
//   2) abre o AddressFormModal real para o endereço principal (POST
//      /customers/:id/addresses).
// Ao concluir, devolve o cliente criado ao pai, que faz o enrich/seleção
// padrão (mesmo caminho de um cliente buscado).
// ===========================================================================
export default function NewCustomerModal({ isOpen, onClose, onCreated }) {
   const [name, setName] = useState('');
   const [doc, setDoc] = useState('');
   const [phone, setPhone] = useState('');
   const [email, setEmail] = useState('');
   const [fieldErrors, setFieldErrors] = useState({});
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [createdCustomer, setCreatedCustomer] = useState(null);
   // AddressFormModal chama onSaved() e em seguida onClose(); o ref evita
   // disparar onCreated duas vezes nesse encadeamento.
   const addressSavedRef = useRef(false);

   const resetAndClose = () => {
      setName('');
      setDoc('');
      setPhone('');
      setEmail('');
      setFieldErrors({});
      setIsSubmitting(false);
      setCreatedCustomer(null);
      onClose?.();
   };

   const handleCreateCustomer = async () => {
      if (isSubmitting) return;

      const errors = {};
      if (!name.trim()) errors.name = 'Informe o nome ou razão social.';
      const docDigits = doc.replace(/\D/g, '');
      if (!docDigits) errors.doc = 'Informe o CPF/CNPJ.';
      if (Object.keys(errors).length > 0) {
         setFieldErrors(errors);
         return;
      }

      setIsSubmitting(true);
      setFieldErrors({});
      try {
         const { customer, message } = await createCustomer({
            name: name.trim(),
            doc: docDigits,
            phone: phone.trim() || null,
            email: email.trim() || null,
            status: 'ATIVO',
         });
         toast.success(message);
         // Etapa 2: AddressFormModal para o endereço principal.
         setCreatedCustomer(customer);
      } catch (err) {
         const responseErrors = err?.response?.data?.errors;
         const httpStatus = err?.response?.status;
         const isUniqueConflict =
            httpStatus === 409 ||
            (Array.isArray(responseErrors) &&
               responseErrors.some((e) => e?.code === 'unique'));
         if (isUniqueConflict) {
            toast.error('CPF/CNPJ já cadastrado. Busque o cliente existente.');
         } else if (Array.isArray(responseErrors) && responseErrors.length > 0) {
            const byField = {};
            responseErrors.forEach((entry) => {
               if (entry?.field) byField[entry.field] = entry.message;
            });
            setFieldErrors(byField);
            toast.error(
               err?.response?.data?.message ?? 'Verifique os campos destacados.',
            );
         } else {
            toast.error(
               err?.response?.data?.message ?? 'Erro ao criar cliente.',
            );
         }
      } finally {
         setIsSubmitting(false);
      }
   };

   // Etapa 2 — endereço principal do cliente recém-criado.
   if (createdCustomer) {
      return (
         <AddressFormModal
            isOpen={isOpen}
            customerId={createdCustomer.id}
            address={{ is_primary: true }}
            onSaved={() => {
               // O cliente já existe; o pai faz o enrich (getCustomer +
               // listAddresses) e seleciona o endereço principal.
               addressSavedRef.current = true;
               onCreated?.(createdCustomer);
            }}
            onClose={() => {
               // Se o operador cancelar o endereço (sem salvar), o cliente já
               // foi criado — devolve mesmo assim para seleção (lista vazia,
               // com aviso para adicionar um endereço). Se já salvou, não
               // dispara de novo (AddressFormModal chama onSaved + onClose).
               if (!addressSavedRef.current) onCreated?.(createdCustomer);
            }}
         />
      );
   }

   const footer = (
      <>
         <GhostButton type="button" onClick={resetAndClose} disabled={isSubmitting}>
            Cancelar
         </GhostButton>
         <PrimaryButton
            type="button"
            onClick={handleCreateCustomer}
            disabled={isSubmitting}
         >
            {isSubmitting ? 'Criando…' : 'Criar e adicionar endereço'}
         </PrimaryButton>
      </>
   );

   return (
      <Modal
         isOpen={isOpen}
         onClose={resetAndClose}
         title="Novo cliente"
         maxWidth="520px"
         footer={footer}
      >
         <p
            style={{
               margin: '0 0 16px',
               fontSize: 13,
               lineHeight: 1.5,
               color: '#666',
            }}
         >
            Dados básicos do cliente. Em seguida você cadastra o endereço
            principal; os demais dados podem ser completados depois na tela do
            cliente.
         </p>
         <FormGrid>
            <Field $span={12}>
               <FieldLabel htmlFor="new-customer-name">
                  Nome / razão social<span className="req">*</span>
               </FieldLabel>
               <FieldInput
                  id="new-customer-name"
                  type="text"
                  maxLength={150}
                  value={name}
                  onChange={(e) => {
                     setName(e.target.value);
                     setFieldErrors((p) => ({ ...p, name: undefined }));
                  }}
                  placeholder="Nome completo do cliente"
                  $invalid={!!fieldErrors.name}
                  disabled={isSubmitting}
               />
               {fieldErrors.name && <FieldError>{fieldErrors.name}</FieldError>}
            </Field>

            <Field $span={6}>
               <FieldLabel htmlFor="new-customer-doc">
                  CPF / CNPJ<span className="req">*</span>
               </FieldLabel>
               <FieldInput
                  id="new-customer-doc"
                  type="text"
                  value={doc}
                  onChange={(e) => {
                     setDoc(e.target.value);
                     setFieldErrors((p) => ({ ...p, doc: undefined }));
                  }}
                  placeholder="000.000.000-00"
                  $invalid={!!fieldErrors.doc}
                  disabled={isSubmitting}
               />
               {fieldErrors.doc && <FieldError>{fieldErrors.doc}</FieldError>}
            </Field>

            <Field $span={6}>
               <FieldLabel htmlFor="new-customer-phone">Telefone</FieldLabel>
               <FieldInput
                  id="new-customer-phone"
                  type="text"
                  maxLength={255}
                  value={phone}
                  onChange={(e) => {
                     setPhone(e.target.value);
                     setFieldErrors((p) => ({ ...p, phone: undefined }));
                  }}
                  placeholder="Ex: 62 9 9999-9999"
                  $invalid={!!fieldErrors.phone}
                  disabled={isSubmitting}
               />
               {fieldErrors.phone && (
                  <FieldError>{fieldErrors.phone}</FieldError>
               )}
            </Field>

            <Field $span={12}>
               <FieldLabel htmlFor="new-customer-email">E-mail</FieldLabel>
               <FieldInput
                  id="new-customer-email"
                  type="email"
                  maxLength={255}
                  value={email}
                  onChange={(e) => {
                     setEmail(e.target.value);
                     setFieldErrors((p) => ({ ...p, email: undefined }));
                  }}
                  placeholder="cliente@email.com"
                  $invalid={!!fieldErrors.email}
                  disabled={isSubmitting}
               />
               {fieldErrors.email ? (
                  <FieldError>{fieldErrors.email}</FieldError>
               ) : (
                  <FieldHint>
                     Telefone e e-mail são opcionais, mas ajudam no contato.
                  </FieldHint>
               )}
            </Field>
         </FormGrid>
      </Modal>
   );
}

NewCustomerModal.propTypes = {
   isOpen: PropTypes.bool,
   onClose: PropTypes.func,
   onCreated: PropTypes.func,
};
